import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2, Heart, ArrowRight, Users, Sparkles } from 'lucide-react';
import { getPaymentStatus, getSuggestedCreators } from '../services/api';
import { toast } from 'sonner';

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');
  
  const [status, setStatus] = useState('checking');
  const [attempts, setAttempts] = useState(0);
  const [showDonationModal, setShowDonationModal] = useState(false);
  const [suggestedCreators, setSuggestedCreators] = useState([]);
  const maxAttempts = 5;

  useEffect(() => {
    if (!sessionId) {
      navigate('/');
      return;
    }

    const checkStatus = async () => {
      try {
        const res = await getPaymentStatus(sessionId);
        
        if (res.data.payment_status === 'paid') {
          setStatus('success');
          toast.success('Payment successful!');
          
          // Fetch suggested creators for donation modal
          try {
            const creatorsRes = await getSuggestedCreators();
            setSuggestedCreators(creatorsRes.data.slice(0, 6));
            // Show donation modal after a short delay
            setTimeout(() => setShowDonationModal(true), 1500);
          } catch (e) {
            console.error('Error fetching creators:', e);
          }
        } else if (res.data.status === 'expired') {
          setStatus('failed');
          toast.error('Payment session expired');
        } else if (attempts < maxAttempts) {
          setAttempts(prev => prev + 1);
          setTimeout(checkStatus, 2000);
        } else {
          setStatus('pending');
        }
      } catch (error) {
        console.error('Error checking payment:', error);
        if (attempts < maxAttempts) {
          setAttempts(prev => prev + 1);
          setTimeout(checkStatus, 2000);
        } else {
          setStatus('error');
        }
      }
    };

    checkStatus();
  }, [sessionId, attempts, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center page-padding" data-testid="payment-success-page">
      <div className="text-center max-w-md">
        {status === 'checking' && (
          <>
            <Loader2 size={64} className="mx-auto text-[var(--primary)] animate-spin mb-4" />
            <h1 className="text-3xl font-teko mb-2">Processing Payment</h1>
            <p className="text-[var(--text-secondary)]">Please wait while we confirm your payment...</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="relative">
              <CheckCircle size={80} className="mx-auto text-green-500 mb-4" />
              <Sparkles className="absolute top-0 right-1/4 text-yellow-500 animate-pulse" size={24} />
            </div>
            <h1 className="text-4xl font-teko mb-2">Welcome to Premium!</h1>
            <p className="text-[var(--text-secondary)] mb-6">Your subscription is now active. You have access to all premium features!</p>
            
            <div className="bg-[var(--surface)] border border-[var(--border)] rounded-lg p-4 mb-6">
              <h3 className="font-teko text-xl mb-2 flex items-center justify-center gap-2">
                <Heart className="text-[var(--primary)]" size={20} />
                Don't Forget to Set Up Donations!
              </h3>
              <p className="text-sm text-[var(--text-secondary)]">
                As a premium member, you can allocate up to $2/month to support your favorite creators.
              </p>
            </div>
            
            <div className="flex flex-col gap-3">
              <button 
                onClick={() => setShowDonationModal(true)}
                className="btn-hotrod w-full"
                data-testid="setup-donations-btn"
              >
                <span className="flex items-center justify-center gap-2">
                  <Heart size={18} />
                  Set Up Donations Now
                </span>
              </button>
              <Link to="/dashboard" className="btn-outline w-full">
                <span>Go to Dashboard</span>
              </Link>
            </div>
          </>
        )}

        {status === 'failed' && (
          <>
            <XCircle size={64} className="mx-auto text-red-500 mb-4" />
            <h1 className="text-3xl font-teko mb-2">Payment Failed</h1>
            <p className="text-[var(--text-secondary)] mb-6">Your payment session has expired or was cancelled.</p>
            <Link to="/pricing" className="btn-hotrod">
              <span>Try Again</span>
            </Link>
          </>
        )}

        {status === 'pending' && (
          <>
            <Loader2 size={64} className="mx-auto text-yellow-500 mb-4" />
            <h1 className="text-3xl font-teko mb-2">Payment Pending</h1>
            <p className="text-[var(--text-secondary)] mb-6">Your payment is still being processed. Check your dashboard for updates.</p>
            <Link to="/dashboard" className="btn-hotrod">
              <span>Go to Dashboard</span>
            </Link>
          </>
        )}

        {status === 'error' && (
          <>
            <XCircle size={64} className="mx-auto text-red-500 mb-4" />
            <h1 className="text-3xl font-teko mb-2">Error</h1>
            <p className="text-[var(--text-secondary)] mb-6">Unable to verify payment status. Please contact support.</p>
            <Link to="/contact" className="btn-outline">
              <span>Contact Support</span>
            </Link>
          </>
        )}
      </div>

      {/* Donation Setup Modal */}
      {showDonationModal && (
        <DonationSetupModal 
          creators={suggestedCreators}
          onClose={() => setShowDonationModal(false)}
          onSetupLater={() => {
            setShowDonationModal(false);
            navigate('/dashboard');
          }}
          onSetupNow={() => {
            setShowDonationModal(false);
            navigate('/donation-setup');
          }}
        />
      )}
    </div>
  );
};

// Donation Setup Modal Component
function DonationSetupModal({ creators, onClose, onSetupLater, onSetupNow }) {
  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div 
        className="bg-[var(--surface)] border border-[var(--border)] rounded-xl max-w-lg w-full overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header with gradient */}
        <div className="bg-gradient-to-r from-[#E63946] to-[#FF6B35] p-6 text-center">
          <Heart className="mx-auto mb-3" size={48} />
          <h2 className="text-3xl font-teko text-white">Support Your Favorite Creators!</h2>
          <p className="text-white/90 text-sm mt-2">
            As a premium member, you can donate up to $2/month to creators you love.
          </p>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="mb-6">
            <h3 className="font-teko text-xl mb-3 flex items-center gap-2">
              <Users size={20} className="text-[var(--primary)]" />
              Popular Creators to Support
            </h3>
            <div className="grid grid-cols-3 gap-3">
              {creators.map(creator => (
                <div key={creator.id} className="text-center p-3 bg-[var(--background)] rounded-lg border border-[var(--border)]">
                  <div className="w-12 h-12 mx-auto rounded-full bg-gradient-to-br from-[#E63946] to-[#FF6B35] flex items-center justify-center text-white font-bold mb-2">
                    {creator.username?.charAt(0).toUpperCase()}
                  </div>
                  <p className="text-sm font-medium truncate">{creator.username}</p>
                  <p className="text-xs text-[var(--text-secondary)]">{creator.comics_count || 0} comics</p>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-[var(--background)] border border-[var(--border)] rounded-lg p-4 mb-6">
            <h4 className="font-medium mb-2">Why Donate?</h4>
            <ul className="text-sm text-[var(--text-secondary)] space-y-1">
              <li>• Support creators directly from your subscription</li>
              <li>• Help them create more amazing content</li>
              <li>• Build a stronger creator community</li>
              <li>• You must select at least 3 creators</li>
            </ul>
          </div>

          <div className="flex gap-3">
            <button 
              onClick={onSetupNow}
              className="btn-hotrod flex-1"
              data-testid="modal-setup-now"
            >
              <span className="flex items-center justify-center gap-2">
                Set Up Now <ArrowRight size={18} />
              </span>
            </button>
            <button 
              onClick={onSetupLater}
              className="btn-outline"
              data-testid="modal-setup-later"
            >
              <span>Later</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PaymentSuccessPage;
